import { useState, type FormEvent } from 'react';
import { loginSchema } from '@colette/shared';
import { C, DEGRADE_ACTION } from '../../theme';
import { api } from '../../api/routes';
import { messageDErreur } from '../../api/useDonnees';
import { enregistrerSession } from '../../session';
import { Champ } from '../composants/Champ';
import { Alerte, CadreAuth, LienTexte } from '../composants/CadreAuth';
import { MotDePasseOublie } from '../composants/MotDePasseOublie';

export function Connexion({
  onConnecte,
  versInscription,
  annonce = null,
}: {
  onConnecte: () => void;
  versInscription: () => void;
  /**
   * Pourquoi on se retrouve ici sans l'avoir demande : « Ta session a expiré ». Absente
   * quand on arrive de soi-meme (premier lancement, deconnexion voulue).
   */
  annonce?: string | null;
}) {
  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    if (envoi) return;

    // Les MEMES schemas Zod que le serveur (packages/shared) : le message affiche ici ne
    // peut pas diverger de la regle appliquee la-bas.
    const verifie = loginSchema.safeParse({ email, password: motDePasse });
    if (!verifie.success) {
      setErreur(verifie.error.issues[0]?.message ?? 'Vérifie tes informations');
      return;
    }

    setEnvoi(true);
    setErreur(null);
    try {
      const reponse = await api.connexion(verifie.data.email, verifie.data.password);
      enregistrerSession(reponse.token, reponse.user);
      onConnecte();
    } catch (cause) {
      setErreur(messageDErreur(cause));
      setEnvoi(false);
    }
  };

  return (
    <CadreAuth
      titre="Content de te revoir"
      sousTitre="Connecte-toi pour retrouver ton foyer."
      pied={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <MotDePasseOublie />
          <div>
            Pas encore de compte ? <LienTexte onClick={versInscription}>Créer un compte</LienTexte>
          </div>
        </div>
      }
    >
      <form onSubmit={soumettre} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Une information, pas une erreur : ni le corail, ni `role="alert"`. */}
        {annonce !== null ? (
          <div
            role="status"
            style={{
              background: C.carte,
              border: `1px solid ${C.bordureForte}`,
              color: C.encre,
              borderRadius: 12,
              padding: '12px 14px',
              fontSize: 13.5,
              fontWeight: 700,
              lineHeight: 1.45,
            }}
          >
            {annonce}
          </div>
        ) : null}
        <Champ
          libelle="Adresse email"
          type="email"
          inputMode="email"
          autoComplete="username"
          valeur={email}
          onChange={setEmail}
        />
        <Champ
          libelle="Mot de passe"
          type="password"
          autoComplete="current-password"
          valeur={motDePasse}
          onChange={setMotDePasse}
        />

        {erreur !== null ? <Alerte message={erreur} /> : null}

        <button
          type="submit"
          disabled={envoi}
          style={{
            height: 52,
            border: 'none',
            borderRadius: 14,
            background: envoi ? C.inactif : DEGRADE_ACTION,
            color: '#FFFFFF',
            fontSize: 16,
            fontWeight: 800,
            fontFamily: 'inherit',
            cursor: envoi ? 'default' : 'pointer',
            marginTop: 4,
          }}
        >
          {envoi ? 'Connexion…' : 'Se connecter'}
        </button>
      </form>
    </CadreAuth>
  );
}
